<script setup>
const { eligibility, event } = defineProps({
  eligibility: Object,
  event: Object,
})

const emit = defineEmits(['update'])

const { t } = useI18n()
const api = useApi()
const selectedCustomer = useSelectedCustomer()
const isAppointment = toRef(() => event?.type === 'appointment')

const step = ref(undefined)
const onlineAccessLink = ref(undefined)

async function getOnlineAccessLink() {
  const linkData = await api.createEventOnlineAccessLink(event.id, { data: { customer: selectedCustomer.value } })
  onlineAccessLink.value = linkData.access_link
}

async function leaveClass() {
  step.value = 'leave-class'
}

onMounted(() => {
  if (event?.venue?.is_online)
    getOnlineAccessLink()
})
</script>

<template>
  <BookingModalLeaveClass
    v-if="step === 'leave-class'"
    :event="event"
    :is-appointment="isAppointment"
    @cancel="step = undefined"
    @update="emit('update')"
  />
  <div
    v-else
    class="flex grow flex-col justify-between flex-gap-y-4 whitespace-pre-wrap"
  >
    <DSAlert
      type="success"
      no-close
      class="w-100%"
    >
      {{ isAppointment ? t('You\'ve booked this appointment') : t('You’re attending this class.') }}
    </DSAlert>
    <div
      class="flex flex-col items-center justify-center flex-gap-y-2"
    >
      <template v-if="event.venue.is_online">
        <a
          class="btn-md w-full truncate text-center uppercase btn-fill-primary"
          :class="{ disabled: !onlineAccessLink }"
          :href="onlineAccessLink"
          target="_blank"
        >
          <span>{{ t('Open Video Link') }}</span>
        </a>
        <p class="text-text-500 fw-600">
          {{ t('or') }}
        </p>
      </template>

      <DSAsyncButton
        class="btn-md w-full uppercase btn-fill-tertiary"
        @submit="leaveClass()"
      >
        <span>{{ isAppointment ? t('Leave Appointment') : t('Leave Class') }}</span>
      </DSAsyncButton>
    </div>
  </div>
</template>

<i18n>
{
  "en-us": {
    "Open Video Link": "Open Video Link",
    "You’re attending this class.": "You’re attending this class.",
    "Leave Class": "Leave Class",
    "You've booked this appointment": "You've booked this appointment"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Open Video Link": "Video-Link öffnen",
  "You’re attending this class.": "Sie besuchen diesen Kurs.",
  "Leave Class": "Kurs verlassen",
  "You've booked this appointment": "Du hast diesen Termin gebucht"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Open Video Link": "Ouvrir le lien de vidéo",
  "You’re attending this class.": "Vous allez participer à ce cours.",
  "Leave Class": "Quitter le cours",
  "You've booked this appointment": "Vous avez réservé ce rendez-vous"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Open Video Link": "Abrir enlace del vídeo",
  "You’re attending this class.": "Usted asistirá a esta clase.",
  "Leave Class": "Abandonar clase",
  "You've booked this appointment": "Has reservado esta cita"
}
</i18n>
